"use client";

import type { IExtProduceTypeVariety } from "@agrigate/product-catalog-types";
import ComboboxSelect from "@agrigate/shared-web-ui/lib/ComboSelect";
import { getProp } from "@agrigate/ts-utils/lib/object";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { useAtomValue } from "jotai";
import type { CSSProperties, ElementType } from "react";
import { useMemo } from "react";
import { produceTypeByIdAtom } from "../stores/produce-types";

export type PTVItem = Pick<IExtProduceTypeVariety, "id" | "name" | "key">;

export default function ProduceTypeVarietiesDropdown(p: {
    name?: string;
    value?: MaybeN<string>;
    defaultValue?: MaybeN<string>;
    produceTypeId?: MaybeN<string>;
    onChange?: (varietyId: MaybeN<string>, variety: MaybeN<PTVItem>) => void;
    autofetchInitial?: MaybeN<boolean>;
    items?: PTVItem[];
    style?: CSSProperties;
    disabled?: boolean;
    customCaret?: ElementType;
    className?: string;
    onClean?: () => void;
    portalled?: boolean;
}) {
    const produceTypesM = useAtomValue(produceTypeByIdAtom);

    const produceType = useMemo(
        () => getProp(produceTypesM, p.produceTypeId),
        [produceTypesM, p.produceTypeId],
    );
    const sourceData = p.items ?? produceType?.varieties?.values ?? [];

    const options = useMemo(
        () =>
            sourceData.map(ptv => ({
                value: ptv.id,
                label: ptv.name ?? ptv.key,
            })),
        [sourceData],
    );
    const currentValue = p.value || p.defaultValue;
    const isDisabled =
        p.disabled ||
        !p.produceTypeId ||
        (!!produceType && options.length === 0);

    return (
        <ComboboxSelect
            items={options}
            value={currentValue ? [currentValue] : []}
            placeholder="Select Variety Type"
            width="100%"
            portalled={p.portalled}
            caretAs={p.customCaret}
            comboRootProps={{
                disabled: isDisabled,
                className: p.className,
                style: p.style,
            }}
            onChange={selectedIds => {
                const newId = selectedIds[0] || null;

                if (!newId) {
                    p.onClean?.();
                    p.onChange?.(null, null);
                    return;
                }

                const fullItem = sourceData.find(i => i.id === newId);
                p.onChange?.(newId, (fullItem as PTVItem) ?? null);
            }}
        />
    );
}
